import {
  RECEIPT_STORAGE_KEY,
  RECEIPT_VALIDITY_MS,
  type ConsentDecision,
  type ConsentReceipt,
} from "./types";

/**
 * Read the stored receipt, or `null` when there is none, it is malformed, it has expired (6 months),
 * or it was recorded against a DIFFERENT `policyVersion` than the one now in force — each of which
 * means the banner must re-prompt (Tech decisions). Pure + SSR-safe (returns null with no `window`).
 */
export function readReceipt(
  policyVersion: string,
  now: number = Date.now(),
): ConsentReceipt | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  const raw = window.localStorage.getItem(RECEIPT_STORAGE_KEY);
  if (!raw) return null;
  let parsed: ConsentReceipt;
  try {
    parsed = JSON.parse(raw) as ConsentReceipt;
  } catch {
    return null;
  }
  if (
    typeof parsed?.recordedAt !== "number" ||
    typeof parsed?.policyVersion !== "string" ||
    typeof parsed?.decision !== "object"
  ) {
    return null;
  }
  // Re-prompt on a policy-text change (the recorded choice no longer matches the current notice).
  if (parsed.policyVersion !== policyVersion) return null;
  // Re-prompt after the 6-month validity window (CNIL).
  if (now - parsed.recordedAt > RECEIPT_VALIDITY_MS) return null;
  return parsed;
}

/** Persist a fresh receipt for the current `policyVersion`. Returns the stored value. */
export function writeReceipt(
  decision: ConsentDecision,
  policyVersion: string,
  now: number = Date.now(),
): ConsentReceipt {
  const receipt: ConsentReceipt = {
    decision,
    policyVersion,
    recordedAt: now,
  };
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.setItem(RECEIPT_STORAGE_KEY, JSON.stringify(receipt));
  }
  return receipt;
}

/** Clear the stored receipt (e.g. a "manage cookies" reset that re-opens the banner). */
export function clearReceipt(): void {
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.removeItem(RECEIPT_STORAGE_KEY);
  }
}
